import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import { Button, Dialog, FormControl, IconButton, TextField, Typography } from '@mui/material';
import { contactFormSchema } from '@softwareIntegrator/schemas/SoftwareIntegratorFormSchema';
import { useSubmitContactFormMutation } from '@softwareIntegrator/services/softwareIntegrators';
import { ISoftwareIntegratorDto } from '@softwareIntegrator/types/softwareIntegrator';
import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import { InferType } from 'yup';

interface IProps {
    open: boolean;
    integrator: ISoftwareIntegratorDto | null;
    onClose?: () => void;
}

export const ContactForm = ({ open, integrator, onClose }: IProps) => {
    const [submitContactForm, { isLoading, isError, isUninitialized, reset: resetSubmission }] =
        useSubmitContactFormMutation();

    const showForm = useMemo(() => isUninitialized || isLoading, [isLoading, isUninitialized]);

    const {
        values,
        touched,
        errors,
        handleSubmit: handleSubscribe,
        handleChange,
    } = useFormik<InferType<typeof contactFormSchema>>({
        initialValues: {
            sender_email: '',
            sender_name: '',
            sender_message: '',
            sender_phone: '',
        },
        onSubmit: async (values, { resetForm }) => {
            await submitContactForm({ ...values, software_integrator: integrator?.slug }).unwrap();
            resetForm();
        },
        validationSchema: contactFormSchema,
    });

    const handleClose = useCallback(
        (e: object, reason: string) => {
            if (reason === 'backdropClick') {
                return;
            }
            onClose?.();
            // Add slight delay to prevent flicker
            setTimeout(resetSubmission, 100);
        },
        [onClose, resetSubmission]
    );

    return (
        <Dialog
            disableEscapeKeyDown
            open={open}
            onClose={handleClose}
            slotProps={{
                paper: { className: 'min-w-0 w-full sm:w-[640px] rounded-2xl p-4' },
            }}
        >
            <div className="text-right">
                <IconButton onClick={(e) => handleClose(e, '')}>
                    <Close />
                </IconButton>
            </div>
            {showForm ? (
                <div className="w-full mt-2 px-2">
                    <Typography variant="h4" className="font-semibold font-museo text-3xl">
                        {integrator ? 'Software Integrator Contact' : "Let's connect"}
                    </Typography>
                    <Typography className="font-inter font-medium text-base mt-2 mb-8">
                        {integrator
                            ? `Directly contact ${integrator.name} by email`
                            : 'Looking to collaborate or learn more? Send us a message.'}
                    </Typography>
                    <FormControl
                        component={'form'}
                        className="flex flex-col gap-6 w-full"
                        onSubmit={handleSubscribe}
                        // Disable default html validation
                        noValidate
                    >
                        <TextField
                            id="sender_name"
                            label="Name"
                            variant="outlined"
                            value={values.sender_name}
                            onChange={handleChange}
                            error={touched.sender_name && Boolean(errors.sender_name)}
                            helperText={touched.sender_name && errors.sender_name}
                            required
                        />
                        <TextField
                            id="sender_email"
                            label="Email"
                            variant="outlined"
                            value={values.sender_email}
                            onChange={handleChange}
                            error={touched.sender_email && Boolean(errors.sender_email)}
                            helperText={touched.sender_email && errors.sender_email}
                            required
                        />
                        <TextField
                            id="sender_phone"
                            label="Phone number"
                            type="tel"
                            variant="outlined"
                            value={values.sender_phone}
                            onChange={handleChange}
                            error={touched.sender_phone && Boolean(errors.sender_phone)}
                            helperText={touched.sender_phone && errors.sender_phone}
                            required
                        />
                        <TextField
                            id="sender_message"
                            label="Message"
                            variant="outlined"
                            multiline
                            value={values.sender_message}
                            onChange={handleChange}
                            error={touched.sender_message && Boolean(errors.sender_message)}
                            helperText={touched.sender_message && errors.sender_message}
                            minRows={8}
                            required
                        />
                        <Button className="block sm:ml-auto sm:w-1/3 rounded-full" type="submit" disabled={isLoading}>
                            {isLoading ? 'Submitting' : 'Submit message'}
                        </Button>
                    </FormControl>
                </div>
            ) : isError ? (
                <div className="sm:w-full my-12 mx-auto">
                    <div className="flex border-2 border-solid rounded-full w-20 h-20 mx-auto">
                        <Close className="text-5xl block m-auto" />
                    </div>
                    <Typography className="font-museo text-3xl sm:text-5xl text-bold text-center my-10">
                        Submission Error
                    </Typography>
                    <Typography className="font-inter text-base sm:text-2xl text-center tracking-wide">
                        A submission error occured.
                    </Typography>
                    <Typography className="font-inter text-base sm:text-2xl text-center tracking-wide">
                        Please try again later.
                    </Typography>
                </div>
            ) : (
                <div className="sm:w-[80%] my-12 mx-auto">
                    <div className="flex border-2 border-solid rounded-full w-20 h-20 mx-auto">
                        <Check className="text-5xl block m-auto" />
                    </div>
                    <Typography className="font-museo text-3xl sm:text-5xl text-bold text-center mt-10 mb-6">
                        Success
                    </Typography>
                    <Typography className="font-inter text-base sm:text-2xl text-center tracking-wide">
                        Your message has been sent!
                    </Typography>
                </div>
            )}
        </Dialog>
    );
};
